import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import {
  Anchor,
  Loader,
  Table,
  Tabs,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IoAddOutline, IoCheckmark, IoClose, IoCreateOutline } from 'react-icons/io5';
import { Breadcrumbs } from '@components';
import {
  marketplaceSettingsApi,
  type MarketplaceSettingsPatch,
} from '@modules/marketplace-settings/api/marketplace-settings.api';
import { sellerOfferTemplatesApi, type SellerOfferTemplate } from '@modules/seller-offer-templates/api/seller-offer-templates.api';
import { productsApi } from '@modules/products/api/products.api';
import { Button, ControlledInput, QuietIconButton } from '@shared/ui';
import { formatDate } from '@shared/utils';
import '@shared/styles/page.css';
import './MarketplaceSettingsPage.css';

type FormState = {
  sellerCommissionPercent?: number;
  acquiringPercent?: number;
  sellerCancellationPenaltyRub?: number;
  sellerRefundPenaltyRub?: number;
  sellerCancelStrikesToBlock?: number;
  rankingWeightCpc?: number;
  rankingWeightOrders?: number;
  rankingWeightRating?: number;
  rankingWeightNew?: number;
  rankingProximityBlend?: number;
  noveltyDaysThreshold?: number;
  cpcClicksPerBillingUnit?: number;
  cpcMaxBidRub?: number;
  cpcMinBidRub?: number;
  cpcClickDedupMinutes?: number;
};

function SettingsEditActions({
  editing,
  saving,
  onEdit,
  onSave,
  onCancel,
}: {
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  if (!editing) {
    return (
      <QuietIconButton
        icon={<IoCreateOutline size={18} />}
        onClick={onEdit}
        disabled={saving}
      >
        Редактировать
      </QuietIconButton>
    );
  }

  return (
    <>
      <QuietIconButton
        icon={<IoCheckmark size={18} />}
        tone="accent"
        onClick={onSave}
        loading={saving}
        aria-label="Сохранить"
      />
      <QuietIconButton
        icon={<IoClose size={18} />}
        onClick={onCancel}
        disabled={saving}
        aria-label="Отменить"
      />
    </>
  );
}

const emptyForm = (): FormState => ({
  sellerCommissionPercent: undefined,
  acquiringPercent: undefined,
  sellerCancellationPenaltyRub: undefined,
  sellerRefundPenaltyRub: undefined,
  sellerCancelStrikesToBlock: undefined,
  rankingWeightCpc: undefined,
  rankingWeightOrders: undefined,
  rankingWeightRating: undefined,
  rankingWeightNew: undefined,
  rankingProximityBlend: undefined,
  noveltyDaysThreshold: undefined,
  cpcClicksPerBillingUnit: undefined,
  cpcMaxBidRub: undefined,
  cpcMinBidRub: undefined,
  cpcClickDedupMinutes: undefined,
});

export default function MarketplaceSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-settings'],
    queryFn: () => marketplaceSettingsApi.get(),
  });
  const { control, reset, getValues } = useForm<FormState>({
    defaultValues: emptyForm(),
  });
  const [settingsTab, setSettingsTab] = useState<string>('commission');
  const [editing, setEditing] = useState(false);
  const fieldsLocked = isLoading || !editing;
  const offerFileInputRef = useRef<HTMLInputElement>(null);

  const { data: offerTemplates, isLoading: offerTemplatesLoading } = useQuery({
    queryKey: ['seller-offer-templates'],
    queryFn: () => sellerOfferTemplatesApi.list(),
    enabled: settingsTab === 'offer',
  });

  const uploadOfferTemplate = useMutation({
    mutationFn: (file: File) => sellerOfferTemplatesApi.upload(file),
    onSuccess: async (template) => {
      const normalized: SellerOfferTemplate = {
        ...template,
        createdAt:
          typeof template.createdAt === 'string'
            ? template.createdAt
            : new Date(template.createdAt).toISOString(),
      };

      queryClient.setQueryData<SellerOfferTemplate[]>(
        ['seller-offer-templates'],
        (prev) => {
          const rest = (prev ?? []).filter(
            (t) => t.id !== normalized.id && t.version !== normalized.version,
          );
          return [normalized, ...rest].sort((a, b) => b.version - a.version);
        },
      );

      if (offerFileInputRef.current) {
        offerFileInputRef.current.value = '';
      }

      await queryClient.refetchQueries({ queryKey: ['seller-offer-templates'] });

      notifications.show({
        title: 'Оферта опубликована',
        message: `Версия v${normalized.version} добавлена. PDF продавца обновится, когда он откроет кабинет.`,
        color: 'green',
        autoClose: 5000,
      });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message ?? 'Не удалось загрузить оферту';
      notifications.show({
        title: 'Ошибка',
        message: Array.isArray(message) ? message.join(', ') : String(message),
        color: 'red',
      });
    },
  });

  const handleOfferFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploadOfferTemplate.isPending) return;
    uploadOfferTemplate.mutate(file);
  };

  useEffect(() => {
    if (!data) return;
    reset({
      sellerCommissionPercent: data.sellerCommissionPercent,
      acquiringPercent: data.acquiringPercent,
      sellerCancellationPenaltyRub: data.sellerCancellationPenaltyRub,
      sellerRefundPenaltyRub: data.sellerRefundPenaltyRub,
      sellerCancelStrikesToBlock: data.sellerCancelStrikesToBlock,
      rankingWeightCpc: data.rankingWeightCpc,
      rankingWeightOrders: data.rankingWeightOrders,
      rankingWeightRating: data.rankingWeightRating,
      rankingWeightNew: data.rankingWeightNew,
      rankingProximityBlend: data.rankingProximityBlend,
      noveltyDaysThreshold: data.noveltyDaysThreshold,
      cpcClicksPerBillingUnit: data.cpcClicksPerBillingUnit,
      cpcMaxBidRub: data.cpcMaxBidRub ?? 500,
      cpcMinBidRub: data.cpcMinBidRub ?? 10,
      cpcClickDedupMinutes: data.cpcClickDedupMinutes ?? 15,
    });
  }, [data, reset]);

  const save = useMutation({
    mutationFn: (body: MarketplaceSettingsPatch) =>
      marketplaceSettingsApi.patch(body),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['marketplace-settings'] });
      queryClient.invalidateQueries({ queryKey: ['seller-sales'] });
      notifications.show({
        title: 'Сохранено',
        message: 'Настройки обновлены',
        color: 'green',
      });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message ?? 'Не удалось сохранить';
      notifications.show({
        title: 'Ошибка',
        message: Array.isArray(message) ? message.join(', ') : String(message),
        color: 'red',
      });
    },
  });

  const recomputeRankingCron = useMutation({
    mutationFn: () => productsApi.runRecomputeCategoryRankingCron(),
    onSuccess: (res) => {
      notifications.show({
        title: 'Пересчёт выполнен',
        message: `Категорий: ${res.categories}, связей: ${res.links}, глобальный ранг: ${res.globalProducts} товар(ов)`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось запустить пересчёт (нужны права супер-админа)',
        color: 'red',
      });
    },
  });

  const parseNum = (v: number | string | undefined | null): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v.replace(',', '.'));
      return Number.isFinite(n) ? n : NaN;
    }
    return NaN;
  };

  const numOr = (
    raw: number | string | undefined | null,
    fallback: number | undefined,
  ): number => {
    const n = parseNum(raw);
    if (!Number.isNaN(n)) return n;
    const f = parseNum(fallback);
    return Number.isNaN(f) ? NaN : f;
  };

  /** Сохранение без HTML5-валидации скрытых вкладок (иначе кнопка «молчит»). */
  const saveSettings = () => {
    const form = getValues();
    const commission = numOr(
      form.sellerCommissionPercent,
      data?.sellerCommissionPercent,
    );
    if (Number.isNaN(commission) || commission < 0 || commission > 100) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Комиссия: число от 0 до 100',
        color: 'red',
      });
      return;
    }
    const acquiring = numOr(form.acquiringPercent, data?.acquiringPercent);
    if (Number.isNaN(acquiring) || acquiring < 0 || acquiring > 100) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Эквайринг: число от 0 до 100',
        color: 'red',
      });
      return;
    }
    const cancellationPenalty = numOr(
      form.sellerCancellationPenaltyRub,
      data?.sellerCancellationPenaltyRub,
    );
    if (
      Number.isNaN(cancellationPenalty) ||
      cancellationPenalty < 0 ||
      cancellationPenalty > 1000000
    ) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Штраф за отмену: число от 0 до 1 000 000 ₽',
        color: 'red',
      });
      return;
    }
    const refundPenalty = numOr(
      form.sellerRefundPenaltyRub,
      data?.sellerRefundPenaltyRub,
    );
    if (
      Number.isNaN(refundPenalty) ||
      refundPenalty < 0 ||
      refundPenalty > 1000000
    ) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Штраф за возврат: число от 0 до 1 000 000 ₽',
        color: 'red',
      });
      return;
    }
    const cancelStrikes = Math.round(
      numOr(form.sellerCancelStrikesToBlock, data?.sellerCancelStrikesToBlock),
    );
    if (
      Number.isNaN(cancelStrikes) ||
      cancelStrikes < 1 ||
      cancelStrikes > 100
    ) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Отмен подряд до блокировки: целое от 1 до 100',
        color: 'red',
      });
      return;
    }

    const wCpc = numOr(form.rankingWeightCpc, data?.rankingWeightCpc);
    const wOrd = numOr(form.rankingWeightOrders, data?.rankingWeightOrders);
    const wRat = numOr(form.rankingWeightRating, data?.rankingWeightRating);
    const wNew = numOr(form.rankingWeightNew, data?.rankingWeightNew);
    for (const [label, w] of [
      ['CPC', wCpc],
      ['Заказы', wOrd],
      ['Рейтинг', wRat],
      ['Новизна', wNew],
    ] as const) {
      if (Number.isNaN(w) || w < 0 || w > 1) {
        notifications.show({
          title: 'Проверьте веса',
          message: `Вес «${label}»: число от 0 до 1`,
          color: 'red',
        });
        return;
      }
    }

    const novelty = Math.round(
      numOr(form.noveltyDaysThreshold, data?.noveltyDaysThreshold),
    );
    if (Number.isNaN(novelty) || novelty < 1 || novelty > 365) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Порог новизны: целое от 1 до 365 дней',
        color: 'red',
      });
      return;
    }

    const clicksUnit = Math.round(
      numOr(form.cpcClicksPerBillingUnit, data?.cpcClicksPerBillingUnit),
    );
    if (Number.isNaN(clicksUnit) || clicksUnit < 1 || clicksUnit > 1000) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Кликов за списание CPC: от 1 до 1000',
        color: 'red',
      });
      return;
    }

    const cpcMaxBid = numOr(form.cpcMaxBidRub, data?.cpcMaxBidRub ?? 500);
    if (Number.isNaN(cpcMaxBid) || cpcMaxBid < 0.01 || cpcMaxBid > 100_000) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Макс. ставка за клик: от 0.01 до 100 000 ₽',
        color: 'red',
      });
      return;
    }

    const cpcMinBid = numOr(form.cpcMinBidRub, data?.cpcMinBidRub ?? 10);
    if (Number.isNaN(cpcMinBid) || cpcMinBid < 0.01 || cpcMinBid > 100_000) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Мин. ставка за клик: от 0.01 до 100 000 ₽',
        color: 'red',
      });
      return;
    }
    if (cpcMinBid > cpcMaxBid) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Минимальная ставка не может быть больше максимальной',
        color: 'red',
      });
      return;
    }

    const dedupMinutes = Math.round(
      numOr(form.cpcClickDedupMinutes, data?.cpcClickDedupMinutes ?? 15),
    );
    if (Number.isNaN(dedupMinutes) || dedupMinutes < 0 || dedupMinutes > 1440) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Дедуп кликов CPC: от 0 до 1440 минут (0 = выкл)',
        color: 'red',
      });
      return;
    }

    const proxBlend = numOr(
      form.rankingProximityBlend,
      data?.rankingProximityBlend,
    );
    if (Number.isNaN(proxBlend) || proxBlend < 0 || proxBlend > 1) {
      notifications.show({
        title: 'Проверьте значение',
        message: 'Доля близости к адресу: число от 0 до 1',
        color: 'red',
      });
      return;
    }

    save.mutate({
      sellerCommissionPercent: commission,
      acquiringPercent: acquiring,
      sellerCancellationPenaltyRub: cancellationPenalty,
      sellerRefundPenaltyRub: refundPenalty,
      sellerCancelStrikesToBlock: cancelStrikes,
      rankingWeightCpc: wCpc,
      rankingWeightOrders: wOrd,
      rankingWeightRating: wRat,
      rankingWeightNew: wNew,
      rankingProximityBlend: proxBlend,
      noveltyDaysThreshold: novelty,
      cpcClicksPerBillingUnit: clicksUnit,
      cpcMaxBidRub: cpcMaxBid,
      cpcMinBidRub: cpcMinBid,
      cpcClickDedupMinutes: dedupMinutes,
    });
  };

  const cancelEdit = () => {
    if (data) {
      reset({
        sellerCommissionPercent: data.sellerCommissionPercent,
        acquiringPercent: data.acquiringPercent,
        sellerCancellationPenaltyRub: data.sellerCancellationPenaltyRub,
        sellerRefundPenaltyRub: data.sellerRefundPenaltyRub,
        sellerCancelStrikesToBlock: data.sellerCancelStrikesToBlock,
        rankingWeightCpc: data.rankingWeightCpc,
        rankingWeightOrders: data.rankingWeightOrders,
        rankingWeightRating: data.rankingWeightRating,
        rankingWeightNew: data.rankingWeightNew,
        rankingProximityBlend: data.rankingProximityBlend,
        noveltyDaysThreshold: data.noveltyDaysThreshold,
        cpcClicksPerBillingUnit: data.cpcClicksPerBillingUnit,
        cpcMaxBidRub: data.cpcMaxBidRub ?? 500,
        cpcMinBidRub: data.cpcMinBidRub ?? 10,
        cpcClickDedupMinutes: data.cpcClickDedupMinutes ?? 15,
      });
    }
    setEditing(false);
  };

  return (
    <form
      className="page-container marketplace-settings-page"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        saveSettings();
      }}
    >
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Настройки' },
        ]}
      />
      <div className="marketplace-settings-head">
        <h1 className="marketplace-settings-title">Настройки</h1>
        <p className="marketplace-settings-subtitle">
          Комиссия, ранжирование, продвижение и оферта продавцов
        </p>
      </div>

      <div className="marketplace-settings-page__scroll">
        <div className="content-wrapper marketplace-settings-page__content-wrapper">
          <div className="marketplace-settings-page__content">
            <Tabs
              value={settingsTab}
              keepMounted={false}
              onChange={(v) => {
                if (editing) cancelEdit();
                setSettingsTab(v ?? 'commission');
              }}
              className="marketplace-settings-page__tabs marketplace-settings-tabs"
            >
              <Tabs.List className="marketplace-settings-page__tabs-list marketplace-settings-tabs-list">
                <Tabs.Tab value="commission" className="marketplace-settings-tabs-tab">
                  Комиссия
                </Tabs.Tab>
                <Tabs.Tab value="ranking" className="marketplace-settings-tabs-tab">
                  Ранжирование
                </Tabs.Tab>
                <Tabs.Tab value="cpc" className="marketplace-settings-tabs-tab">
                  Продвижение
                </Tabs.Tab>
                <Tabs.Tab value="offer" className="marketplace-settings-tabs-tab">
                  Оферта
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="commission" className="marketplace-settings-page__tabs-panel marketplace-settings-tabs-panel">
                <section className="marketplace-settings-card">
                  <div className="marketplace-settings-card__head">
                    <h2 className="marketplace-settings-card__title">Комиссия</h2>
                    <div className="marketplace-settings-card__actions">
                      <SettingsEditActions
                        editing={editing}
                        saving={save.isPending}
                        onEdit={() => setEditing(true)}
                        onSave={saveSettings}
                        onCancel={cancelEdit}
                      />
                    </div>
                  </div>
                  <p className="marketplace-settings-note">
                    Комиссия маркетплейса считается с товаров продавца (после
                    промокода и бонусов), эквайринг — со всей онлайн-оплаты клиента,
                    включая доставку. При отмене оплаченного заказа до выдачи
                    эквайринг тоже удерживается с продавца. Проценты фиксируются в
                    заказе при его создании.
                  </p>
                  <div className="marketplace-settings-page__inputs-grid">
                    <ControlledInput
                      id="sellerCommissionPercent"
                      type="number"
                      label="Комиссия маркетплейса, %"
                      description="С суммы товаров, без эквайринга. От 0 до 100"
                      min="0"
                      max="100"
                      step="0.01"
                      name="sellerCommissionPercent"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="acquiringPercent"
                      type="number"
                      label="Эквайринг, %"
                      description="Со всей онлайн-оплаты (товары + доставка), по тарифу Т‑Банка. От 0 до 100"
                      min="0"
                      max="100"
                      step="0.01"
                      name="acquiringPercent"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="sellerCancellationPenaltyRub"
                      type="number"
                      label="Штраф за отмену заказа продавцом, ₽"
                      description="Если заказ ещё не был выполнен (не DELIVERED). Удерживается при расчёте выплат"
                      min="0"
                      max="1000000"
                      step="0.01"
                      name="sellerCancellationPenaltyRub"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="sellerRefundPenaltyRub"
                      type="number"
                      label="Штраф за возврат выполненного заказа, ₽"
                      description="Если заказ уже был DELIVERED, а потом возврат/отмена. Сейчас 0"
                      min="0"
                      max="1000000"
                      step="0.01"
                      name="sellerRefundPenaltyRub"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="sellerCancelStrikesToBlock"
                      type="number"
                      label="Отмен подряд до блокировки кабинета"
                      description="Ручная отмена продавцом или автоотмена за бездействие. По умолчанию 2"
                      min="1"
                      max="100"
                      step="1"
                      name="sellerCancelStrikesToBlock"
                      control={control}
                      disabled={fieldsLocked}
                    />
                  </div>
                  {data?.updatedAt ? (
                    <p className="marketplace-settings-note marketplace-settings-note--flush">
                      Последнее изменение: {formatDate(data.updatedAt)}
                    </p>
                  ) : null}
                </section>
              </Tabs.Panel>

              <Tabs.Panel value="ranking" className="marketplace-settings-page__tabs-panel marketplace-settings-tabs-panel">
                <section className="marketplace-settings-card">
                  <div className="marketplace-settings-card__head">
                    <h2 className="marketplace-settings-card__title">Ранжирование в каталоге</h2>
                    <div className="marketplace-settings-card__actions">
                      <SettingsEditActions
                        editing={editing}
                        saving={save.isPending}
                        onEdit={() => setEditing(true)}
                        onSave={saveSettings}
                        onCancel={cancelEdit}
                      />
                    </div>
                  </div>
                  <div className="marketplace-settings-page__inputs-grid">
                    <ControlledInput
                      id="rankingWeightCpc"
                      type="number"
                      label="Вес CPC (ставка за клик)"
                      description="0…1. Дефолт 0.5"
                      min="0"
                      max="1"
                      step="0.05"
                      name="rankingWeightCpc"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="rankingWeightOrders"
                      type="number"
                      label="Вес заказов за 30 дней"
                      description="0…1. Дефолт 0.35"
                      min="0"
                      max="1"
                      step="0.05"
                      name="rankingWeightOrders"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="rankingWeightRating"
                      type="number"
                      label="Вес рейтинга"
                      description="0…1. Дефолт 0.1"
                      min="0"
                      max="1"
                      step="0.05"
                      name="rankingWeightRating"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="rankingWeightNew"
                      type="number"
                      label="Вес новизны"
                      description="0…1. Дефолт 0.05"
                      min="0"
                      max="1"
                      step="0.05"
                      name="rankingWeightNew"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="noveltyDaysThreshold"
                      type="number"
                      label="Бонус новизны: товар не старше, дней"
                      description="1…365"
                      min="1"
                      max="365"
                      step="1"
                      name="noveltyDaysThreshold"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="rankingProximityBlend"
                      type="number"
                      label="Доля близости к адресу пользователя"
                      description="0…1."
                      min="0"
                      max="1"
                      step="0.05"
                      name="rankingProximityBlend"
                      control={control}
                      disabled={fieldsLocked}
                    />
                  </div>
                  <p className="marketplace-settings-note marketplace-settings-note--flush">
                    Для проверки без ожидания расписания: тот же пересчёт, что запускает серверный cron (по умолчанию
                    каждые 6 ч.).
                  </p>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => recomputeRankingCron.mutate()}
                    loading={recomputeRankingCron.isPending}
                  >
                    Запустить пересчёт рангов
                  </Button>
                  {data?.updatedAt ? (
                    <p className="marketplace-settings-note marketplace-settings-note--flush">
                      Последнее изменение: {formatDate(data.updatedAt)}
                    </p>
                  ) : null}
                </section>
              </Tabs.Panel>

              <Tabs.Panel value="cpc" className="marketplace-settings-page__tabs-panel marketplace-settings-tabs-panel">
                <section className="marketplace-settings-card">
                  <div className="marketplace-settings-card__head">
                    <h2 className="marketplace-settings-card__title">Продвижение (CPC)</h2>
                    <div className="marketplace-settings-card__actions">
                      <SettingsEditActions
                        editing={editing}
                        saving={save.isPending}
                        onEdit={() => setEditing(true)}
                        onSave={saveSettings}
                        onCancel={cancelEdit}
                      />
                    </div>
                  </div>
                  <p className="marketplace-settings-note">
                    Клики списываются с рекламного баланса продавца. Если на ставку
                    товара не хватает баланса, продвижение этого товара останавливается.
                  </p>
                  <div className="marketplace-settings-page__inputs-grid">
                    <ControlledInput
                      id="cpcClicksPerBillingUnit"
                      type="number"
                      label="Кликов за одно списание CPC"
                      description="1 = каждый клик"
                      min="1"
                      max="1000"
                      step="1"
                      name="cpcClicksPerBillingUnit"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="cpcMinBidRub"
                      type="number"
                      label="Мин. ставка за клик, ₽"
                      description="Ниже нельзя включить продвижение; в ранге как 0. Дефолт 10"
                      min="0.01"
                      max="100000"
                      step="1"
                      name="cpcMinBidRub"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="cpcMaxBidRub"
                      type="number"
                      label="Макс. ставка за клик, ₽"
                      description="Потолок CPC и силы буста в ранге. Дефолт 500"
                      min="0.01"
                      max="100000"
                      step="1"
                      name="cpcMaxBidRub"
                      control={control}
                      disabled={fieldsLocked}
                    />
                    <ControlledInput
                      id="cpcClickDedupMinutes"
                      type="number"
                      label="Дедуп кликов, минут"
                      description="Один зритель — не чаще 1 клика на товар за это время. 15 = сессия. 0 = выкл"
                      min="0"
                      max="1440"
                      step="1"
                      name="cpcClickDedupMinutes"
                      control={control}
                      disabled={fieldsLocked}
                    />
                  </div>
                  {data?.updatedAt ? (
                    <p className="marketplace-settings-note marketplace-settings-note--flush">
                      Последнее изменение: {formatDate(data.updatedAt)}
                    </p>
                  ) : null}
                </section>
              </Tabs.Panel>

              <Tabs.Panel value="offer" className="marketplace-settings-page__tabs-panel marketplace-settings-tabs-panel">
                <section className="marketplace-settings-card">
                  <div className="marketplace-settings-card__head">
                    <h2 className="marketplace-settings-card__title">Договор-оферта для продавцов</h2>
                    <QuietIconButton
                      icon={<IoAddOutline size={18} />}
                      disabled={offerTemplatesLoading || uploadOfferTemplate.isPending}
                      loading={uploadOfferTemplate.isPending}
                      onClick={() => offerFileInputRef.current?.click()}
                    >
                      Опубликовать новую версию
                    </QuietIconButton>
                  </div>
                  <p className="marketplace-settings-note">
                    Загрузите PDF с текстом оферты. В месте «Реквизиты
                    Заказчика» вставьте одной строкой плейсхолдер{' '}
                    <code className="marketplace-settings-code">{'{{CUSTOMER_REQUISITES}}'}</code>
                    — при генерации он заменится на реквизиты продавца. Если плейсхолдера нет, блок допишется в конец
                    последней страницы. После публикации новой версии продавцам
                    нужно повторно принять условия.
                  </p>

                  <input
                    ref={offerFileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={handleOfferFileSelected}
                  />

                  {offerTemplatesLoading ? (
                    <div className="marketplace-settings-empty">
                      <Loader size="sm" />
                    </div>
                  ) : offerTemplates && offerTemplates.length > 0 ? (
                    <div className="marketplace-settings-table-wrap">
                      <Table className="marketplace-settings-table">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Версия</Table.Th>
                            <Table.Th>Дата обновления</Table.Th>
                            <Table.Th>PDF</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {offerTemplates.map((t, index) => (
                            <Table.Tr key={t.id}>
                              <Table.Td>
                                <span className="marketplace-settings-version">
                                  v{t.version}
                                  {index === 0 ? (
                                    <span className="marketplace-settings-badge">текущая</span>
                                  ) : null}
                                </span>
                              </Table.Td>
                              <Table.Td>{formatDate(t.createdAt)}</Table.Td>
                              <Table.Td>
                                <Anchor
                                  href={t.pdfFileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="marketplace-settings-link"
                                >
                                  Открыть
                                </Anchor>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="marketplace-settings-note marketplace-settings-note--flush">
                      Шаблон ещё не загружен. До первой загрузки при регистрации
                      используется встроенный черновик оферты.
                    </p>
                  )}
                </section>
              </Tabs.Panel>
            </Tabs>
          </div>
        </div>
      </div>
    </form>
  );
}
